import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "components/button";
import { Icon } from "components/icon/Icon";
import { Text } from "components/text";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { useAnalytics } from "contexts/AnalyticsContext";
import { useProductAvailability } from "hooks/useProductAvailability";
import { useSalesData } from "contexts/SalesDataContext";
import { useBundleFormats } from "../../data/useBundleFormats";
import { BundleDrawer } from "./BundleDrawer";
import { DisabledBundleButton } from "./DisabledBundleButton";
import { MIN_BUNDLE_FORMATS, orderFormats } from "./bundle.utils";

interface BundleButtonProps {
  workId?: string | null;
  currentIsbn: string;
}

export const BundleButton = ({ workId, currentIsbn }: BundleButtonProps) => {
  const { t } = useTranslation(["productDetails"]);
  const { gaEvent } = useAnalytics();
  const { bundleIsbns, setBundleIsbns, isBundled } = useSalesData();
  const [open, setOpen] = useState(false);
  // Restore draft after turn-off then re-open.
  const [lastSelectedIsbns, setLastSelectedIsbns] = useState<string[]>([]);

  const { isProductActive } = useProductAvailability();
  const { formats: workFormats, isLoading, isError } = useBundleFormats(workId);
  const formats = orderFormats(workFormats, currentIsbn, isProductActive);
  const formatCount = workFormats.length;
  const label = t("salesData.bundle.button");

  if (!workId) {
    return (
      <DisabledBundleButton
        label={label}
        tooltip={{
          title: t("salesData.bundle.tooltip.unavailableTitle"),
          body: t("salesData.bundle.tooltip.unavailableBody"),
        }}
      />
    );
  }

  // Quiet while loading; on a fetch error stay enabled (below) so the drawer can
  // surface the error rather than claim "nothing to bundle".
  if (!isBundled && isLoading) {
    return <DisabledBundleButton label={label} />;
  }
  if (!isBundled && !isError && formatCount < MIN_BUNDLE_FORMATS) {
    return (
      <DisabledBundleButton
        label={label}
        tooltip={{
          title: t("salesData.bundle.tooltip.noFormatsTitle"),
          body: t("salesData.bundle.tooltip.noFormatsBody"),
        }}
      />
    );
  }

  const handleConfirm = (isbns: string[]) => {
    const wasBundled = isBundled;
    setLastSelectedIsbns(isbns);
    setBundleIsbns(isbns);
    if (wasBundled) {
      gaEvent({
        type: "sales_bundle_formats_changed",
        payload: { work_id: workId, selected_format_count: isbns.length },
      });
    } else {
      gaEvent({
        type: "sales_bundle_toggled",
        payload: { enabled: true, work_id: workId, format_count: isbns.length },
      });
    }
    setOpen(false);
  };

  const handleTurnOff = () => {
    gaEvent({
      type: "sales_bundle_toggled",
      payload: {
        enabled: false,
        work_id: workId,
        format_count: bundleIsbns.length,
      },
    });
    setBundleIsbns([]);
    setOpen(false);
  };

  const initialSelection = isBundled ? bundleIsbns : lastSelectedIsbns;

  return (
    <>
      {isBundled ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-primary px-4 py-3 text-sm font-medium text-primary-600 shadow-xs transition-colors hover:bg-primary-50"
          data-testid="bundle-button"
        >
          <Icon name="layers" size="medium" aria-hidden />
          {t("salesData.bundle.buttonActive", { count: bundleIsbns.length })}
          <span className="h-5 w-px shrink-0 bg-primary/20" aria-hidden />
          <Icon
            name="settings2"
            size="medium"
            aria-hidden
            data-testid="bundle-settings"
          />
        </button>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                variant="tertiary"
                icon="layers"
                iconPosition="left"
                label={label}
                onClick={() => setOpen(true)}
                testId="bundle-button"
              />
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            collisionPadding={16}
            className="max-w-xs border-secondary-200 bg-white text-secondary-800 shadow-md"
          >
            <div className="flex items-start gap-2">
              <Icon
                name="layers"
                size="small"
                iconClassName="mt-0.5 shrink-0 text-secondary-400"
              />
              <span className="flex flex-col gap-0.5">
                <Text as="span" variant="label">
                  {t("salesData.bundle.tooltip.availableTitle")}
                </Text>
                <Text as="span" variant="muted">
                  {t("salesData.bundle.tooltip.availableBody")}
                </Text>
              </span>
            </div>
          </TooltipContent>
        </Tooltip>
      )}

      <BundleDrawer
        open={open}
        onOpenChange={setOpen}
        currentIsbn={currentIsbn}
        initialSelection={initialSelection}
        isEditing={isBundled}
        formats={formats}
        isLoading={isLoading}
        isError={isError}
        onConfirm={handleConfirm}
        onTurnOff={handleTurnOff}
      />
    </>
  );
};
